import { ChangeDetectionStrategy, Component, ElementRef, input, model, viewChildren } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { TermScope } from '../../app/shared/models/term-scope.model';

/** One segment's wire value + i18n label key, in render order. */
interface TermScopeOption {
  readonly value: TermScope;
  readonly labelKey: string;
}

/** Fixed, three-option set — this is deliberately NOT a generic segmented
    control (ADR D5: do not generalise with `by-state-tab`'s pill group). */
const OPTIONS: readonly TermScopeOption[] = [
  { value: 'all', labelKey: 'termScope.all' },
  { value: 'perpetual', labelKey: 'termScope.perpetual' },
  { value: 'fixed', labelKey: 'termScope.fixedTerm' },
];

/**
 * All / Perpetual / Fixed term segmented selector — shared by Workspace and
 * Reports (ADR D5, `docs/superpowers/specs/2026-08-19-term-scope-selector-design.md`).
 *
 * Presentational only: no store injection, no fetch, no persistent state. A
 * per-page store owns the actual `term`/`appliedTerm` signals and decides what
 * a scope change does (ADR D3); this control only reflects/emits a value —
 * same division of responsibility as `app-filter-date`/`app-filter-select`.
 *
 * Renders a WAI-ARIA `radiogroup` with roving tabindex: the selected segment is
 * the group's one tab stop (`tabindex="0"`), the other two are `tabindex="-1"`.
 * Arrow keys move (and select) with wraparound; Home/End jump to the first/last
 * segment. Selection follows focus, the standard radiogroup convention — both
 * click and keyboard selection funnel through the single {@link select} method.
 */
@Component({
  selector: 'app-term-scope-selector',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './app-term-scope-selector.component.html',
  styleUrl: './app-term-scope-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppTermScopeSelectorComponent {
  /** Selected scope. `model()` gives `[(value)]` + an auto `valueChange` output. */
  readonly value = model<TermScope>('all');

  /** Disables all three segments. */
  readonly disabled = input(false);

  /** The three segments, in render order. */
  readonly options = OPTIONS;

  /** The rendered `role="radio"` buttons, in the same order as {@link options} —
      used to move focus after a keyboard-driven selection. */
  private readonly radioButtons = viewChildren<ElementRef<HTMLButtonElement>>('radioButton');

  /**
   * Selects `next`. A re-click/re-select of the ALREADY-selected segment is a
   * no-op — no emit, matching the store-side `setTerm` idempotency this control
   * is a presentational mirror of (ADR D3).
   */
  select(next: TermScope): void {
    if (next === this.value()) {
      return;
    }
    this.value.set(next);
  }

  /**
   * Roving-tabindex keyboard navigation for the group: ArrowRight/ArrowDown to
   * the next segment (wrapping), ArrowLeft/ArrowUp to the previous (wrapping),
   * Home to the first, End to the last. Selection follows focus — the standard
   * radiogroup pattern — so a move both selects the target segment (via
   * {@link select}) and moves DOM focus onto it.
   */
  onKeydown(event: KeyboardEvent): void {
    if (this.disabled()) {
      return;
    }
    const currentIndex = this.options.findIndex((o) => o.value === this.value());
    const lastIndex = this.options.length - 1;
    let nextIndex: number;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = lastIndex;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.select(this.options[nextIndex].value);
    this.radioButtons()[nextIndex]?.nativeElement.focus();
  }
}
